import {convertToError, getCalculationError} from './errors.utils.v1';
import {HttpError} from '@wix/http-client';
import {ErrorData} from '../../../../types/types';
import {CheckoutErrorCodeV1, ClientErrorCode, PlaceOrderErrorCode} from '../../../utils/errors';
import {ILocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {MinimumOrderErrorData} from '../../../services/checkout/CheckoutService';
import {Details} from '@wix/auto_sdk_ecom_cart-v-2';

export enum Action {
  BackToSite = 'BackToSite',
  ReturnToCart = 'ReturnToCart',
  ReturnToCheckout = 'ReturnToCheckout',
  GotIt = 'GotIt',
}

export class ModalErrorModel {
  public biCode: string;
  public title: string;
  public description: string;
  public action: Action;

  constructor(params: ModalErrorModel) {
    this.biCode = params.biCode;
    this.title = params.title;
    this.description = params.description;
    this.action = params.action;
  }

  public static fromCheckoutV1(httpError: HttpError, localeKeys: ILocaleKeys) {
    const errorDataDetails = (httpError?.response?.data as ErrorData)?.details;
    const error = convertToError(errorDataDetails);
    return new ModalErrorModel({
      biCode: error.code,
      ...toErrorDataFromErrorV1({error, localeKeys}),
    });
  }

  public static fromCartV2(httpError: HttpError, localeKeys: ILocaleKeys) {
    const error = (httpError?.response?.data as {details: Details})?.details;
    const calculationError = getCalculationError(error);
    if (calculationError) {
      return new ModalErrorModel({
        biCode: calculationError.code,
        ...toErrorDataFromErrorV1({error: calculationError, localeKeys}),
      });
    }
    const applicationError = error.applicationError;
    return new ModalErrorModel({
      biCode: applicationError?.code ?? /* istanbul ignore next */ 'GENERAL_ERROR',
      ...toErrorDataFromErrorV2({error: applicationError, localeKeys}),
    });
  }

  public static fromClientError({
    code,
    data,
    localeKeys,
  }: {
    code: keyof typeof ClientErrorCode;
    data?: Record<string, any> | null;
    localeKeys: ILocaleKeys;
  }) {
    return new ModalErrorModel({
      biCode: code,
      ...toErrorDataFromClientError({code, data}, localeKeys),
    });
  }
}

function toErrorDataFromClientError(
  error: {code: keyof typeof ClientErrorCode; data?: Record<string, any> | null},
  localeKeys: ILocaleKeys
) {
  switch (error.code) {
    case ClientErrorCode.CLIENT_FULL_OUT_OF_STOCK:
      return {
        title: localeKeys.cart.validation.error.FULL_OUT_OF_STOCK.title(),
        description: localeKeys.cart.validation.error.FULL_OUT_OF_STOCK.content(),
        action: Action.BackToSite,
      };
    case ClientErrorCode.CLIENT_PAYMENT_ERROR:
      return {
        title: localeKeys.checkout.informative_modal.wallet_payment_problem(),
        description: error.data?.translatedError,
        action: Action.ReturnToCheckout,
      };
    case ClientErrorCode.CLIENT_CART_CHANGED:
      return {
        title: localeKeys.checkout.errorModal.totalPriceChanged.title(),
        description: localeKeys.checkout.errorModal.totalPriceChanged.subtitle(),
        action: Action.GotIt,
      };
    case ClientErrorCode.CLIENT_EMPTY_CART:
    default:
      return {
        title: localeKeys.cart.validation.error.EMPTY_CART.title(),
        description: localeKeys.cart.validation.error.EMPTY_CART.content(),
        action: Action.BackToSite,
      };
  }
}

function toErrorDataFromErrorV2({
  error,
  localeKeys,
}: {
  error?: {code?: string; data?: Record<string, any> | null};
  localeKeys: ILocaleKeys;
}) {
  if (error?.code === PlaceOrderErrorCode.EMPTY_CART) {
    return {
      title: localeKeys.cart.validation.error.EMPTY_CART.title(),
      description: localeKeys.cart.validation.error.EMPTY_CART.content(),
      action: Action.BackToSite,
    };
  }
  if (error?.code === PlaceOrderErrorCode.ALL_LINE_ITEMS_OUT_OF_STOCK) {
    return {
      title: localeKeys.cart.validation.error.FULL_OUT_OF_STOCK.title(),
      description: localeKeys.cart.validation.error.FULL_OUT_OF_STOCK.content(),
      action: Action.BackToSite,
    };
  }
  if (error?.code === PlaceOrderErrorCode.SOME_LINE_ITEMS_OUT_OF_STOCK) {
    return {
      title: localeKeys.cart.validation.error.PARTIAL_OUT_OF_STOCK.title(),
      description: localeKeys.cart.validation.error.PARTIAL_OUT_OF_STOCK.content(),
      action: Action.GotIt,
    };
  }
  if (error?.code === PlaceOrderErrorCode.VIOLATIONS_WITH_ERROR_SEVERITY) {
    return {
      title: localeKeys.checkout.errorModal.validationsSPI.title(),
      description: localeKeys.checkout.errorModal.validationsSPI.subtitle(),
      action: Action.GotIt,
    };
  }
  if (error?.code === PlaceOrderErrorCode.CALCULATION_ERROR) {
    return {
      title: localeKeys.checkout.page.technicalCalculationError.title(),
      description: localeKeys.checkout.page.technicalCalculationError.subtitle(),
      action: Action.GotIt,
    };
  }
  if (error?.code === PlaceOrderErrorCode.CANNOT_PAY_FOR_GIFT_CARD_WITH_GIFT_CARD) {
    return {
      title: localeKeys.checkout.giftCardError.giftCardCantPurchaseGiftCard.title(),
      description: localeKeys.checkout.giftCardError.giftCardCantPurchaseGiftCard.subtitle(),
      action: Action.ReturnToCheckout,
    };
  }
  if (error?.code === PlaceOrderErrorCode.CANNOT_REDEEM_COUPON_WHEN_PURCHASING_GIFT_CARD) {
    return {
      title: localeKeys.checkout.giftCardError.couponCantPurchaseGiftCard.title(),
      description: localeKeys.checkout.giftCardError.couponCantPurchaseGiftCard.subtitle(),
      action: Action.ReturnToCheckout,
    };
  }
  if (error?.code === PlaceOrderErrorCode.PAYMENT_ALREADY_IN_PROGRESS) {
    return {
      title: localeKeys.cart.validation.error.PAYMENT_IN_PROCESS.title(),
      description: localeKeys.cart.validation.error.PAYMENT_IN_PROCESS.content(),
      action: Action.GotIt,
    };
  }
  if (error?.code === PlaceOrderErrorCode.CART_PRICES_OUT_OF_SYNC) {
    return {
      title: localeKeys.checkout.errorModal.totalPriceChanged.title(),
      description: localeKeys.checkout.errorModal.totalPriceChanged.subtitle(),
      action: Action.GotIt,
    };
  }
  if (error?.code === PlaceOrderErrorCode.MISSING_DELIVERY_METHOD) {
    return {
      title: localeKeys.checkout.informative_modal.unsupported_shipping_destination_title(),
      description: localeKeys.checkout.informative_modal.unsupported_shipping_destination_title(),
      action: Action.GotIt,
    };
  }
  return {
    title: localeKeys.checkout.informative_modal.cashier_internal_error_title(),
    description: localeKeys.checkout.informative_modal.cashier_internal_error(),
    action: Action.ReturnToCart,
  };
}

function toErrorDataFromErrorV1({
  error,
  localeKeys,
}: {
  error: {code: string; data?: Record<string, any> | null};
  localeKeys: ILocaleKeys;
}) {
  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (error.code) {
    case CheckoutErrorCodeV1.EMPTY_CHECKOUT:
      return {
        title: localeKeys.cart.validation.error.EMPTY_CART.title(),
        description: localeKeys.cart.validation.error.EMPTY_CART.content(),
        action: Action.BackToSite,
      };
    case CheckoutErrorCodeV1.FULL_OUT_OF_STOCK:
      return {
        title: localeKeys.cart.validation.error.FULL_OUT_OF_STOCK.title(),
        description: localeKeys.cart.validation.error.FULL_OUT_OF_STOCK.content(),
        action: Action.BackToSite,
      };
    case CheckoutErrorCodeV1.PARTIAL_OUT_OF_STOCK:
      return {
        title: localeKeys.cart.validation.error.PARTIAL_OUT_OF_STOCK.title(),
        description: localeKeys.cart.validation.error.PARTIAL_OUT_OF_STOCK.content(),
        action: Action.GotIt,
      };
    case CheckoutErrorCodeV1.GIFT_CARD_CODE_INVALID:
      return {
        title: localeKeys.checkout.orderFailedModal.invalidCardError.title(),
        description: localeKeys.checkout.orderFailedModal.invalidCardError.content(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.GIFT_CARD_EXPIRED:
      return {
        title: localeKeys.checkout.orderFailedModal.expiredCardError.title(),
        description: localeKeys.checkout.orderFailedModal.expiredCardError.content(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.GIFT_CARD_INSUFFICIENT_BALANCE:
      return {
        title: localeKeys.checkout.orderFailedModal.balanceChangedError.title(),
        description: localeKeys.checkout.orderFailedModal.balanceChangedError.content(),
        action: Action.ReturnToCheckout,
      };

    case CheckoutErrorCodeV1.GIFT_CARD_IS_EMPTY:
      return {
        title: localeKeys.checkout.orderFailedModal.insufficientFundsError.title(),
        description: localeKeys.checkout.orderFailedModal.insufficientFundsError.content(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.GIFT_CARD_CANNOT_BE_USED_TO_PURCHASE_GIFT_CARD:
      return {
        title: localeKeys.checkout.giftCardError.giftCardCantPurchaseGiftCard.title(),
        description: localeKeys.checkout.giftCardError.giftCardCantPurchaseGiftCard.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.GIFT_CARD_UNSUPPORTED_STATUS:
    case CheckoutErrorCodeV1.GIFT_CARD_GENERAL_ERROR:
      return {
        title: localeKeys.checkout.orderFailedModal.genericFallbackError.title(),
        description: localeKeys.checkout.orderFailedModal.genericFallbackError.content(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.GIFT_CARD_CURRENCY_INVALID:
      return {
        title: localeKeys.checkout.paymentDetails.enterGiftCard.error.replaceCurrency.title(),
        description: localeKeys.checkout.paymentDetails.enterGiftCard.error.replaceCurrency.content(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.GIFT_CARD_DISABLED:
      return {
        title: localeKeys.checkout.paymentDetails.enterGiftCard.error.genericFallback.$value(),
        description: '',
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.GIFT_CARD_SERVICE_UNAVAILABLE:
      return {
        title: localeKeys.checkout.paymentDetails.enterGiftCard.error.serviceDown.title(),
        description: localeKeys.checkout.paymentDetails.enterGiftCard.error.serviceDown.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.ERROR_INVALID_COMMAND_FIELD:
    case CheckoutErrorCodeV1.ERROR_COUPON_DOES_NOT_EXIST:
      return {
        title: localeKeys.checkout.promoCodeError.doesntExist.title(),
        description: localeKeys.checkout.promoCodeError.doesntExist.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.ERROR_COUPON_IS_NOT_ACTIVE_YET:
      return {
        title: localeKeys.checkout.promoCodeError.notActiveYet.title(),
        description: localeKeys.checkout.promoCodeError.notActiveYet.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.ERROR_COUPON_IS_DISABLED:
      return {
        title: localeKeys.checkout.promoCodeError.disabled.title(),
        description: localeKeys.checkout.promoCodeError.disabled.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.COUPON_CANNOT_BE_USED_TO_PURCHASE_GIFT_CARD:
      return {
        title: localeKeys.checkout.giftCardError.couponCantPurchaseGiftCard.title(),
        description: localeKeys.checkout.giftCardError.couponCantPurchaseGiftCard.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.ERROR_COUPON_USAGE_EXCEEDED:
    case CheckoutErrorCodeV1.ERROR_COUPON_LIMIT_PER_CUSTOMER_EXCEEDED:
      return {
        title: localeKeys.checkout.promoCodeError.exceededUsage.title(),
        description: localeKeys.checkout.promoCodeError.exceededUsage.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.ERROR_COUPON_HAS_EXPIRED:
      return {
        title: localeKeys.checkout.promoCodeError.expired.title(),
        description: localeKeys.checkout.promoCodeError.expired.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.ERROR_INVALID_SUBTOTAL:
      return {
        title: localeKeys.checkout.promoCodeError.invalidSubtotal.title({
          subtotal: error.data?.description,
        }),
        description: localeKeys.checkout.promoCodeError.invalidSubtotal.subtitle(),
        action: Action.BackToSite,
      };
    case CheckoutErrorCodeV1.ERROR_INVALID_PRODUCTS:
      return {
        title: localeKeys.checkout.promoCodeError.invalidProducts.title(),
        description: localeKeys.checkout.promoCodeError.invalidProducts.subtitle(),
        action: Action.BackToSite,
      };
    case CheckoutErrorCodeV1.ERROR_INVALID_PRODUCT_QUANTITY:
      return {
        title: localeKeys.checkout.promoCodeError.invalidProductQuantity.title(),
        description: localeKeys.checkout.promoCodeError.invalidProductQuantity.subtitle({
          quantity: error.data?.description,
        }),
        action: Action.BackToSite,
      };
    case CheckoutErrorCodeV1.ERROR_NOT_APPLICABLE_FOR_SUBSCRIPTIONS:
      return {
        title: localeKeys.checkout.promoCodeError.subscriptionsNotValid.title(),
        description: localeKeys.checkout.promoCodeError.subscriptionsNotValid.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.NO_SELECTED_CARRIER_SERVICE_OPTION:
      return {
        title: localeKeys.checkout.informative_modal.unsupported_shipping_destination_title(),
        description: localeKeys.checkout.informative_modal.unsupported_shipping_destination(),
        action: Action.ReturnToCart,
      };
    case CheckoutErrorCodeV1.MINIMUM_ORDER_AMOUNT_NOT_REACHED:
      return {
        title: localeKeys.checkout.minimumOrderModal.delivery.withPickup.title({
          minAmount: (error.data as MinimumOrderErrorData).minimumOrderAmount.formattedConvertedAmount,
        }),
        description: localeKeys.checkout.minimumOrderModal.withPickup.body({
          additionalAmount: (error.data as MinimumOrderErrorData).remaining.formattedConvertedAmount,
        }),
        action: Action.GotIt,
      };
    case CheckoutErrorCodeV1.CHECKOUT_PAYMENT_ALREADY_IN_PROGRESS:
      return {
        title: localeKeys.cart.validation.error.PAYMENT_IN_PROCESS.title(),
        description: localeKeys.cart.validation.error.PAYMENT_IN_PROCESS.content(),
        action: Action.GotIt,
      };
    case CheckoutErrorCodeV1.EWALLET_ERROR:
      return {
        title: localeKeys.checkout.page.generalPaymentError.title(),
        description: localeKeys.checkout.page.generalPaymentError.subtitle(),
        action: Action.GotIt,
      };
    case CheckoutErrorCodeV1.CALCULATION_ERROR:
      return {
        title: localeKeys.checkout.page.technicalCalculationError.title(),
        description: localeKeys.checkout.page.technicalCalculationError.subtitle(),
        action: Action.GotIt,
      };
    case CheckoutErrorCodeV1.CHECKOUT_CHANGED:
      return {
        title: localeKeys.checkout.errorModal.totalPriceChanged.title(),
        description: localeKeys.checkout.errorModal.totalPriceChanged.subtitle(),
        action: Action.GotIt,
      };
    /* istanbul ignore next */
    case CheckoutErrorCodeV1.CUSTOM_PAYMENT_ERROR:
    case CheckoutErrorCodeV1.PAYMENT_ERROR:
      return {
        title: localeKeys.checkout.informative_modal.wallet_payment_problem(),
        description: error.data!.translatedError,
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.VIOLATIONS_WITH_ERROR_SEVERITY:
      return {
        title: localeKeys.checkout.errorModal.validationsSPI.title(),
        description: localeKeys.checkout.errorModal.validationsSPI.subtitle(),
        action: Action.GotIt,
      };
    case CheckoutErrorCodeV1.NON_SUFFICIENT_FUNDS:
      return {
        title: localeKeys.checkout.bookingsError.insufficientPlanSessions.title(),
        description: localeKeys.checkout.bookingsError.insufficientPlanSessions.subtitle(),
        action: Action.ReturnToCheckout,
      };
    case CheckoutErrorCodeV1.GENERAL_ERROR:
    default:
      return {
        title: localeKeys.checkout.informative_modal.cashier_internal_error_title(),
        description: localeKeys.checkout.informative_modal.cashier_internal_error(),
        action: Action.ReturnToCart,
      };
  }
}
